import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaInfoCircle, FaMapMarkerAlt } from 'react-icons/fa';
import {
  bootstrapPickupHubsFromRestaurants,
  createPickupHub,
  fetchHubSchools,
  fetchHubTransfers,
  fetchPickupHubs,
  fetchRestaurantsAvailableAsHubs,
  mapSchoolToHub,
  saveHubTransfers,
  unmapSchoolFromHub,
  updatePickupHub,
} from './pickupHubsApi';
import axiosOrganizationInstance from '../organization/AxiosOrganizationInstance';
import { getAuthHeaders } from '../organization/organizationStorage';
import {
  extractOrganizationId,
  extractOrganizationName,
} from '../organization/organizationApi';
import { fetchRestaurantById } from '../restaurant/restaurantApi';
import PickupPricingPage from './PickupPricingPage';
import './EatsAdminCreatePage.css';
import './PickupHubsPage.css';

function hubIdOf(hub) {
  return hub?.hub_id ?? hub?.hubId ?? null;
}

function hubNameOf(hub) {
  return hub?.hub_name || hub?.hubName || `Hub ${hubIdOf(hub) || ''}`;
}

function schoolOrgId(row) {
  return row?.organization_id ?? row?.organizationId ?? null;
}

function schoolNameOf(row) {
  return (
    row?.organization_name ||
    row?.organizationName ||
    extractOrganizationName(row) ||
    `School ${schoolOrgId(row) || ''}`
  );
}

function toCoord(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const a = toCoord(lat1);
  const b = toCoord(lon1);
  const c = toCoord(lat2);
  const d = toCoord(lon2);
  if (a == null || b == null || c == null || d == null) return null;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const r = 6371;
  const dLat = toRad(c - a);
  const dLon = toRad(d - b);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a)) * Math.cos(toRad(c)) * Math.sin(dLon / 2) ** 2;
  return Math.round(r * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x)) * 100) / 100;
}

function pickAddressFromList(addresses) {
  const list = Array.isArray(addresses) ? addresses : addresses ? [addresses] : [];
  return list[0] || null;
}

function formatAddressText(addr) {
  if (!addr) return '';
  const parts = [
    addr.addressLine1 ?? addr.address_line1 ?? addr.line1,
    addr.addressLine2 ?? addr.address_line2 ?? addr.line2,
    addr.areaName ?? addr.area_name,
    addr.city,
    addr.state,
    addr.postalCode ?? addr.postal_code ?? addr.postalCode,
    addr.country,
  ]
    .map((p) => (p != null ? String(p).trim() : ''))
    .filter(Boolean);
  return parts.join(', ');
}

function coordsFromAddress(addr) {
  if (!addr) return { lat: null, lon: null };
  return {
    lat: toCoord(addr.lat ?? addr.latitude),
    lon: toCoord(addr.lon ?? addr.longitude ?? addr.lng),
  };
}

function formatDistanceKm(km) {
  if (km == null || !Number.isFinite(Number(km))) return null;
  return `${Number(km).toFixed(2)} km`;
}

function mapsUrlFromCoordsOrAddress(lat, lon, addressText) {
  const a = toCoord(lat);
  const b = toCoord(lon);
  if (a != null && b != null) {
    return `https://www.google.com/maps?q=${a},${b}`;
  }
  const query = String(addressText || '').trim();
  if (!query || query === 'Address not available') return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function mapsEmbedUrlFromCoordsOrAddress(lat, lon, addressText) {
  const a = toCoord(lat);
  const b = toCoord(lon);
  if (a != null && b != null) {
    return `https://maps.google.com/maps?q=${a},${b}&z=16&output=embed`;
  }
  const query = String(addressText || '').trim();
  if (!query || query === 'Address not available') return null;
  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=16&output=embed`;
}

function MapPreviewModal({ open, title, embedUrl, externalUrl, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open || !embedUrl) return null;

  return (
    <div className="pickup-hubs-map-modal" role="presentation" onClick={onClose}>
      <div
        className="pickup-hubs-map-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Map preview'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pickup-hubs-map-modal__header">
          <div className="pickup-hubs-map-modal__title">{title || 'Map preview'}</div>
          <button
            type="button"
            className="pickup-hubs-map-modal__close"
            aria-label="Close map"
            title="Close"
            onClick={onClose}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="pickup-hubs-map-modal__body">
          <iframe
            title={title || 'Map preview'}
            src={embedUrl}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
        {externalUrl ? (
          <div className="pickup-hubs-map-modal__footer">
            <a href={externalUrl} target="_blank" rel="noopener noreferrer">
              Open in Google Maps
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MapsLink({ lat, lon, addressText, label = 'Open in Maps', onOpen }) {
  const embedUrl = mapsEmbedUrlFromCoordsOrAddress(lat, lon, addressText);
  const externalUrl = mapsUrlFromCoordsOrAddress(lat, lon, addressText);
  if (!embedUrl) return null;
  return (
    <button
      type="button"
      className="pickup-hubs-maps-link"
      aria-label={label}
      title={label}
      onClick={() =>
        onOpen?.({
          title: label,
          embedUrl,
          externalUrl,
        })
      }
    >
      <FaMapMarkerAlt aria-hidden="true" />
    </button>
  );
}

function InfoHint({ label, children, align = 'start' }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div
      className={`pickup-hubs-info${align === 'end' ? ' pickup-hubs-info--end' : ''}`}
      ref={rootRef}
    >
      <button
        type="button"
        className="pickup-hubs-info-btn"
        aria-label={label}
        aria-expanded={open}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        <FaInfoCircle aria-hidden="true" />
      </button>
      {open ? (
        <div className="pickup-hubs-info-popover" role="dialog" aria-label={label}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

const HUB_TABS = [
  { id: 'restaurants', label: 'Map Restaurants' },
  { id: 'schools', label: 'Map Schools' },
  { id: 'hub2hub', label: 'Hub2Hub' },
  { id: 'pricing', label: 'Pickup Pricing' },
];

const PickupHubsPage = () => {
  const [activeTab, setActiveTab] = useState('restaurants');
  const [hubs, setHubs] = useState([]);
  const [availableRestaurants, setAvailableRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('');
  const [selectedHubId, setSelectedHubId] = useState('');
  const [hubQuery, setHubQuery] = useState('');
  const [hubMenuOpen, setHubMenuOpen] = useState(false);
  const hubPickerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [adding, setAdding] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    is_active: true,
    accepts_home_pickup: true,
    accepts_transfer_in: true,
    allows_transfer_out: false,
    service_radius_km: '15',
    daily_capacity: '100',
  });
  const [partnerIds, setPartnerIds] = useState([]);

  const [mappedSchools, setMappedSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [schoolRadiusKm, setSchoolRadiusKm] = useState('5');
  const [schoolResults, setSchoolResults] = useState([]);
  const [searchingSchools, setSearchingSchools] = useState(false);
  const [mappingSchool, setMappingSchool] = useState(false);
  const [schoolActionId, setSchoolActionId] = useState(null);
  const [hubPlace, setHubPlace] = useState(null);
  const [loadingHubPlace, setLoadingHubPlace] = useState(false);
  const [mapPreview, setMapPreview] = useState(null);

  const selectedHub = useMemo(
    () => hubs.find((h) => String(hubIdOf(h)) === String(selectedHubId)) || null,
    [hubs, selectedHubId],
  );

  const filteredHubs = useMemo(() => {
    const q = hubQuery.trim().toLowerCase();
    if (!q) return hubs;
    return hubs.filter((hub) => {
      const name = String(hubNameOf(hub)).toLowerCase();
      const id = String(hubIdOf(hub) || '');
      return name.includes(q) || id.includes(q);
    });
  }, [hubs, hubQuery]);

  useEffect(() => {
    if (!selectedHub) {
      if (!hubMenuOpen) setHubQuery('');
      return;
    }
    if (!hubMenuOpen) {
      setHubQuery(hubNameOf(selectedHub));
    }
  }, [selectedHub, hubMenuOpen]);

  useEffect(() => {
    if (!hubMenuOpen) return undefined;
    const onDocClick = (event) => {
      if (hubPickerRef.current && !hubPickerRef.current.contains(event.target)) {
        setHubMenuOpen(false);
        if (selectedHub) setHubQuery(hubNameOf(selectedHub));
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [hubMenuOpen, selectedHub]);

  const otherHubs = useMemo(
    () => hubs.filter((h) => String(hubIdOf(h)) !== String(selectedHubId)),
    [hubs, selectedHubId],
  );

  const mappedSchoolIds = useMemo(
    () => new Set(mappedSchools.map((s) => String(schoolOrgId(s))).filter(Boolean)),
    [mappedSchools],
  );

  const combinedSchoolRows = useMemo(() => {
    const byId = new Map();

    for (const school of mappedSchools) {
      const orgId = schoolOrgId(school);
      if (!orgId) continue;
      const lat = toCoord(school.address?.lat);
      const lon = toCoord(school.address?.lon);
      const distanceKm =
        toCoord(school.distance_km ?? school.distanceKm) ??
        (hubPlace ? haversineKm(hubPlace.lat, hubPlace.lon, lat, lon) : null);
      byId.set(String(orgId), {
        key: `mapped-${orgId}`,
        orgId,
        name: schoolNameOf(school),
        addressText: formatAddressText(school.address) || 'Address not available',
        lat,
        lon,
        distanceKm,
        mapped: true,
        isPrimary: school.is_primary === true,
      });
    }

    for (const school of schoolResults) {
      const orgId = extractOrganizationId(school);
      if (!orgId || mappedSchoolIds.has(String(orgId))) continue;
      const addr = pickAddressFromList(school.addresses);
      const coords = coordsFromAddress(addr);
      const apiDist = toCoord(school.distanceKm ?? school.distance_km);
      const distanceKm =
        apiDist != null
          ? apiDist
          : hubPlace
            ? haversineKm(hubPlace.lat, hubPlace.lon, coords.lat, coords.lon)
            : null;
      byId.set(String(orgId), {
        key: `search-${orgId}`,
        orgId,
        name: schoolNameOf(school),
        addressText: formatAddressText(addr) || 'Address not available',
        lat: coords.lat,
        lon: coords.lon,
        distanceKm,
        mapped: false,
        isPrimary: false,
      });
    }

    return Array.from(byId.values()).sort((a, b) => {
      if (a.mapped !== b.mapped) return a.mapped ? -1 : 1;
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      if (a.distanceKm == null && b.distanceKm == null) return 0;
      if (a.distanceKm == null) return 1;
      if (b.distanceKm == null) return -1;
      return a.distanceKm - b.distanceKm;
    });
  }, [mappedSchools, schoolResults, hubPlace, mappedSchoolIds]);

  const loadAvailableRestaurants = useCallback(async (hubList) => {
    setLoadingRestaurants(true);
    try {
      const rows = await fetchRestaurantsAvailableAsHubs(hubList);
      setAvailableRestaurants(rows);
      setSelectedRestaurantId((prev) => {
        if (prev && rows.some((r) => String(r.restaurant_id) === String(prev))) {
          return prev;
        }
        return '';
      });
    } catch (err) {
      setAvailableRestaurants([]);
      setSelectedRestaurantId('');
      setError(
        err?.response?.data?.error_message ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load restaurants',
      );
    } finally {
      setLoadingRestaurants(false);
    }
  }, []);

  const loadHubSchools = useCallback(async (hubId) => {
    if (!hubId) {
      setMappedSchools([]);
      return;
    }
    setLoadingSchools(true);
    try {
      const rows = await fetchHubSchools(hubId);
      setMappedSchools(rows);
    } catch (err) {
      setMappedSchools([]);
      setError(
        err?.response?.data?.error_message ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load mapped schools',
      );
    } finally {
      setLoadingSchools(false);
    }
  }, []);

  const loadHubs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await fetchPickupHubs();
      setHubs(list);
      setSelectedHubId((prev) => {
        if (prev && list.some((h) => String(hubIdOf(h)) === String(prev))) {
          return prev;
        }
        return '';
      });
      await loadAvailableRestaurants(list);
    } catch (err) {
      setError(err?.response?.data?.error_message || err?.message || 'Failed to load hubs');
    } finally {
      setLoading(false);
    }
  }, [loadAvailableRestaurants]);

  useEffect(() => {
    loadHubs();
  }, [loadHubs]);

  useEffect(() => {
    if (!selectedHub) {
      setMappedSchools([]);
      setSchoolResults([]);
      setHubPlace(null);
      setSchoolSearch('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setError('');
      setForm({
        is_active: selectedHub.is_active !== false,
        accepts_home_pickup: selectedHub.accepts_home_pickup !== false,
        accepts_transfer_in: selectedHub.accepts_transfer_in !== false,
        allows_transfer_out: selectedHub.allows_transfer_out === true,
        service_radius_km: String(selectedHub.service_radius_km ?? 15),
        daily_capacity: String(selectedHub.daily_capacity ?? 100),
      });

      const restaurantId = selectedHub.restaurant_id ?? selectedHub.restaurantId;
      setLoadingHubPlace(true);
      try {
        const [transfers] = await Promise.all([
          fetchHubTransfers(hubIdOf(selectedHub), 'out'),
          loadHubSchools(hubIdOf(selectedHub)),
          (async () => {
            if (!restaurantId) {
              if (!cancelled) setHubPlace(null);
              return;
            }
            try {
              const restaurant = await fetchRestaurantById(restaurantId);
              if (cancelled) return;
              const addr = pickAddressFromList(restaurant?.addresses);
              const coords = coordsFromAddress(addr);
              setHubPlace({
                restaurantId,
                name:
                  restaurant?.restaurantName ||
                  restaurant?.restaurant_name ||
                  hubNameOf(selectedHub),
                addressText: formatAddressText(addr) || 'Address not available',
                ...coords,
              });
            } catch (placeErr) {
              if (!cancelled) {
                setHubPlace({
                  restaurantId,
                  name: hubNameOf(selectedHub),
                  addressText: 'Address not available',
                  lat: null,
                  lon: null,
                });
              }
            }
          })(),
        ]);
        if (cancelled) return;
        setPartnerIds(
          transfers
            .filter((t) => t.is_active !== false)
            .map((t) => Number(t.to_hub_id ?? t.toHubId))
            .filter((id) => Number.isFinite(id)),
        );
      } catch (err) {
        if (!cancelled) {
          setPartnerIds([]);
          setError(err?.response?.data?.error_message || err?.message || 'Failed to load transfers');
        }
      } finally {
        if (!cancelled) setLoadingHubPlace(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedHub, loadHubSchools]);

  const togglePartner = (id) => {
    const num = Number(id);
    setPartnerIds((prev) =>
      prev.includes(num) ? prev.filter((x) => x !== num) : [...prev, num],
    );
  };

  const handleAddRestaurantAsHub = async () => {
    if (!selectedRestaurantId) return;
    setAdding(true);
    setMessage('');
    setError('');
    try {
      const result = await createPickupHub({ restaurant_id: Number(selectedRestaurantId) });
      const created = result?.hub;
      const newHubId = hubIdOf(created);
      setMessage(result?.message || 'Hub created from restaurant.');
      const list = await fetchPickupHubs();
      setHubs(list);
      if (newHubId) {
        setSelectedHubId(String(newHubId));
      } else if (list.length) {
        setSelectedHubId(String(hubIdOf(list[list.length - 1])));
      }
      await loadAvailableRestaurants(list);
    } catch (err) {
      setError(
        err?.response?.data?.error_message ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to add restaurant as hub',
      );
    } finally {
      setAdding(false);
    }
  };

  const handleBootstrapMissing = async () => {
    setBootstrapping(true);
    setMessage('');
    setError('');
    try {
      const result = await bootstrapPickupHubsFromRestaurants();
      const list = Array.isArray(result?.hubs) ? result.hubs : await fetchPickupHubs();
      setHubs(list);
      setSelectedHubId((prev) => {
        if (prev && list.some((h) => String(hubIdOf(h)) === String(prev))) return prev;
        return '';
      });
      setMessage(
        result?.message ||
          `Created ${result?.created_count ?? 0} hub(s) from active restaurants.`,
      );
      await loadAvailableRestaurants(list);
    } catch (err) {
      setError(
        err?.response?.data?.error_message ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to bootstrap hubs',
      );
    } finally {
      setBootstrapping(false);
    }
  };

  const handleSearchSchools = useCallback(async (opts = {}) => {
    const { silentEmpty = false } = opts;
    if (!hubPlace?.lat || !hubPlace?.lon) {
      if (!silentEmpty) {
        setError('Hub restaurant address/coords are required to search nearby schools.');
      }
      return;
    }
    const name = schoolSearch.trim();
    const radius = Number(schoolRadiusKm);
    if (!Number.isFinite(radius) || radius <= 0) {
      if (!silentEmpty) setError('Radius must be a positive number (km).');
      return;
    }
    setSearchingSchools(true);
    if (!silentEmpty) setError('');
    try {
      const res = await axiosOrganizationInstance.get('/organization/search', {
        params: {
          ...(name ? { name } : {}),
          lat: hubPlace.lat,
          lon: hubPlace.lon,
          radiusKm: radius,
        },
        headers: getAuthHeaders(),
      });
      const rows = Array.isArray(res?.data) ? res.data : res?.data?.organizations || [];
      setSchoolResults(rows);
      if (!rows.length && !silentEmpty) {
        setError(`No schools found within ${radius} km${name ? ` matching “${name}”` : ''}.`);
      }
    } catch (err) {
      setSchoolResults([]);
      if (!silentEmpty) {
        setError(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Failed to search schools',
        );
      }
    } finally {
      setSearchingSchools(false);
    }
  }, [hubPlace, schoolSearch, schoolRadiusKm]);

  // Pre-load nearby schools (default 5km) when hub is ready; re-search as name/radius changes.
  useEffect(() => {
    if (activeTab !== 'schools' || !selectedHub || !hubPlace?.lat || !hubPlace?.lon) {
      return undefined;
    }
    const hasName = Boolean(schoolSearch.trim());
    const delay = hasName ? 350 : 0;
    const timer = setTimeout(() => {
      handleSearchSchools({ silentEmpty: true });
    }, delay);
    return () => clearTimeout(timer);
  }, [
    activeTab,
    selectedHub,
    hubPlace?.lat,
    hubPlace?.lon,
    schoolSearch,
    schoolRadiusKm,
    handleSearchSchools,
  ]);

  const handleMapSchool = async (organizationId) => {
    if (!selectedHub || !organizationId) return;
    setMappingSchool(true);
    setSchoolActionId(String(organizationId));
    setMessage('');
    setError('');
    try {
      const hubId = hubIdOf(selectedHub);
      const makePrimary = mappedSchools.length === 0;
      const result = await mapSchoolToHub(hubId, {
        organization_id: Number(organizationId),
        is_primary: makePrimary,
      });
      setMessage(result?.message || 'School added to mapped list.');
      await loadHubSchools(hubId);
    } catch (err) {
      setError(
        err?.response?.data?.error_message ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to map school',
      );
    } finally {
      setMappingSchool(false);
      setSchoolActionId(null);
    }
  };

  const handleSetPrimary = async (organizationId) => {
    if (!selectedHub || !organizationId) return;
    setSchoolActionId(String(organizationId));
    setMessage('');
    setError('');
    try {
      const hubId = hubIdOf(selectedHub);
      await mapSchoolToHub(hubId, {
        organization_id: Number(organizationId),
        is_primary: true,
      });
      setMessage('Primary school hub updated.');
      await loadHubSchools(hubId);
    } catch (err) {
      setError(
        err?.response?.data?.error_message ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to set primary',
      );
    } finally {
      setSchoolActionId(null);
    }
  };

  const handleUnmapSchool = async (organizationId) => {
    if (!selectedHub || !organizationId) return;
    setSchoolActionId(String(organizationId));
    setMessage('');
    setError('');
    try {
      const hubId = hubIdOf(selectedHub);
      await unmapSchoolFromHub(hubId, organizationId);
      setMessage('School unmapped from hub.');
      await loadHubSchools(hubId);
    } catch (err) {
      setError(
        err?.response?.data?.error_message ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to unmap school',
      );
    } finally {
      setSchoolActionId(null);
    }
  };

  const handleSave = async () => {
    if (!selectedHub) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const hubId = hubIdOf(selectedHub);
      const radius = Number(form.service_radius_km);
      const capacity = Number(form.daily_capacity);
      if (!Number.isFinite(radius) || radius <= 0) {
        throw new Error('Service radius must be a positive number');
      }
      if (!Number.isFinite(capacity) || capacity <= 0) {
        throw new Error('Daily capacity must be a positive number');
      }
      await updatePickupHub(hubId, {
        is_active: form.is_active,
        accepts_home_pickup: form.accepts_home_pickup,
        accepts_transfer_in: form.accepts_transfer_in,
        allows_transfer_out: form.allows_transfer_out || partnerIds.length > 0,
        service_radius_km: radius,
        daily_capacity: Math.floor(capacity),
      });
      await saveHubTransfers(hubId, partnerIds);
      setMessage('Hub capabilities and transfer partners saved.');
      await loadHubs();
    } catch (err) {
      setError(err?.response?.data?.error_message || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const closeMapPreview = useCallback(() => setMapPreview(null), []);

  const busy =
    loading || saving || adding || bootstrapping || mappingSchool || searchingSchools;

  const introByTab = {
    restaurants:
      'Promote ACTIVE restaurants into pickup hubs. One restaurant maps to one hub.',
    schools:
      'Select a hub to load nearby schools (5 km). Mapped schools show Remove; others show Add. Type to filter by name.',
    hub2hub:
      'Configure hub capabilities and which hubs may transfer boxes to which partners. Route B only uses allowlisted partners.',
    pricing:
      'Configure global and per-hub lunchbox fees, then preview quotes for distance, route type, and cutoff surcharge.',
  };

  const schoolsHelp =
    'Schools: Selecting a hub loads nearby schools within the radius (default 5 km), including already mapped ones. Mapped rows show Remove; others show Add. First mapped school becomes primary.';

  const needsHub = activeTab === 'schools' || activeTab === 'hub2hub';

  const hubToolbar = (
    <div className="pickup-hubs-toolbar">
      <div className="pickup-hubs-field pickup-hubs-field--grow" ref={hubPickerRef}>
        <span>Hub</span>
        <div className="pickup-hubs-typeahead">
          <input
            type="text"
            value={hubQuery}
            placeholder={hubs.length ? 'Type to find a hub' : 'No hubs yet'}
            disabled={loading || !hubs.length}
            onFocus={() => setHubMenuOpen(true)}
            onChange={(e) => {
              setHubQuery(e.target.value);
              setHubMenuOpen(true);
              if (selectedHubId) setSelectedHubId('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setHubMenuOpen(false);
                if (selectedHub) setHubQuery(hubNameOf(selectedHub));
              }
            }}
            autoComplete="off"
          />
          {hubMenuOpen && hubs.length ? (
            <div className="pickup-hubs-typeahead__menu" role="listbox">
              {filteredHubs.length ? (
                filteredHubs.map((hub) => {
                  const id = String(hubIdOf(hub));
                  return (
                    <button
                      key={id}
                      type="button"
                      role="option"
                      className={`pickup-hubs-typeahead__option${
                        id === String(selectedHubId) ? ' is-selected' : ''
                      }`}
                      onClick={() => {
                        setSelectedHubId(id);
                        setHubQuery(hubNameOf(hub));
                        setHubMenuOpen(false);
                      }}
                    >
                      {hubNameOf(hub)}
                    </button>
                  );
                })
              ) : (
                <div className="pickup-hubs-typeahead__empty">No hubs match</div>
              )}
            </div>
          ) : null}
        </div>
      </div>
      {activeTab === 'schools' && selectedHub ? (
        <>
          <label className="pickup-hubs-field pickup-hubs-field--grow">
            Search school
            <input
              type="text"
              value={schoolSearch}
              placeholder="Filter by school name"
              onChange={(e) => setSchoolSearch(e.target.value)}
              disabled={busy || loadingHubPlace}
              autoComplete="off"
            />
          </label>
          <label className="pickup-hubs-field">
            Radius (km)
            <input
              type="number"
              min="1"
              step="0.5"
              value={schoolRadiusKm}
              onChange={(e) => setSchoolRadiusKm(e.target.value)}
              disabled={busy || loadingHubPlace}
            />
          </label>
        </>
      ) : null}
    </div>
  );

  return (
    <div className="pickup-hubs-page">
      <div className="pickup-hubs-header-row">
        <div className="pickup-hubs-tabs" role="tablist" aria-label="Pickup hubs sections">
          {HUB_TABS.map((tab) => {
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`pickup-hubs-tab${selected ? ' pickup-hubs-tab--active' : ''}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  setMessage('');
                  setError('');
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <InfoHint
          align="end"
          label={`${HUB_TABS.find((t) => t.id === activeTab)?.label || 'Tab'} info`}
        >
          {introByTab[activeTab]}
        </InfoHint>
      </div>

      {error && activeTab !== 'pricing' ? (
        <div className="pickup-hubs-error">{error}</div>
      ) : null}
      {message && activeTab !== 'pricing' ? (
        <div className="pickup-hubs-success">{message}</div>
      ) : null}

      {activeTab === 'restaurants' ? (
        <section className="pickup-hubs-section pickup-hubs-add">
          <div className="pickup-hubs-section-heading">
            <h3>Add restaurant as hub</h3>
            <InfoHint label="Map restaurants info">
              Shows ACTIVE restaurants that are not already hubs. One restaurant maps to one hub.
            </InfoHint>
          </div>
          <div className="pickup-hubs-toolbar">
            <label className="pickup-hubs-field pickup-hubs-field--grow">
              Restaurant
              <select
                value={selectedRestaurantId}
                onChange={(e) => setSelectedRestaurantId(e.target.value)}
                disabled={busy || loadingRestaurants || !availableRestaurants.length}
              >
                <option value="">
                  {loadingRestaurants
                    ? 'Loading restaurants…'
                    : availableRestaurants.length
                      ? 'Select a restaurant'
                      : 'No restaurants available'}
                </option>
                {availableRestaurants.map((r) => (
                  <option key={r.restaurant_id} value={String(r.restaurant_id)}>
                    {r.restaurant_name} (#{r.restaurant_id})
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="eats-admin-btn eats-admin-btn--primary"
              onClick={handleAddRestaurantAsHub}
              disabled={busy || !selectedRestaurantId}
            >
              {adding ? 'Adding…' : 'Add as hub'}
            </button>
            <button
              type="button"
              className="eats-admin-btn eats-admin-btn--secondary"
              onClick={handleBootstrapMissing}
              disabled={busy || !availableRestaurants.length}
              title="Create hubs for all ACTIVE restaurants missing a hub"
            >
              {bootstrapping ? 'Creating…' : 'Add all missing'}
            </button>
          </div>

          {loading ? (
            <div className="pickup-hubs-note" style={{ marginTop: 12 }}>
              Loading hubs…
            </div>
          ) : hubs.length ? (
            <div className="pickup-hubs-note" style={{ marginTop: 12 }}>
              Current hubs: {hubs.map((h) => hubNameOf(h)).join(', ')}
            </div>
          ) : (
            <div className="pickup-hubs-note" style={{ marginTop: 12 }}>
              No pickup hubs yet.
            </div>
          )}
        </section>
      ) : null}

      {needsHub ? hubToolbar : null}

      {needsHub && loading ? (
        <div className="pickup-hubs-note">Loading hubs…</div>
      ) : null}

      {needsHub && !loading && !selectedHub ? (
        <div className="pickup-hubs-note">
          {hubs.length
            ? 'Type to find a hub, then select one to continue.'
            : 'No pickup hubs yet. Use Map Restaurants to add a restaurant as a hub first.'}
        </div>
      ) : null}

      {activeTab === 'schools' && selectedHub ? (
        <section className="pickup-hubs-section">
          <div className="pickup-hubs-compare pickup-hubs-compare--with-map">
            <div className="pickup-hubs-compare__col">
              <div className="pickup-hubs-compare__label">Hub restaurant</div>
              <div className="pickup-hubs-compare__name">
                {loadingHubPlace
                  ? 'Loading address…'
                  : hubPlace?.name || hubNameOf(selectedHub)}
              </div>
              <div className="pickup-hubs-compare__address">
                {loadingHubPlace ? '…' : hubPlace?.addressText || 'Address not available'}
              </div>
            </div>
            {!loadingHubPlace ? (
              <div className="pickup-hubs-compare__map">
                <MapsLink
                  lat={hubPlace?.lat}
                  lon={hubPlace?.lon}
                  addressText={hubPlace?.addressText}
                  label={hubPlace?.name || 'Restaurant on Maps'}
                  onOpen={setMapPreview}
                />
              </div>
            ) : null}
          </div>

          <div className="pickup-hubs-section-heading">
            <h3>Schools</h3>
            <InfoHint label="Schools info">{schoolsHelp}</InfoHint>
          </div>

          {searchingSchools || loadingSchools || loadingHubPlace ? (
            <div className="pickup-hubs-note">Loading schools…</div>
          ) : !combinedSchoolRows.length ? (
            <div className="pickup-hubs-note">
              No schools found within {schoolRadiusKm || 5} km. Try a larger radius or name filter.
            </div>
          ) : (
            <div className="pickup-hubs-table-wrap">
              <table className="pickup-hubs-table">
                <thead>
                  <tr>
                    <th>School</th>
                    <th>Address</th>
                    <th>Distance</th>
                    <th>Role</th>
                    <th className="pickup-hubs-table__icon-col" aria-label="Map" />
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {combinedSchoolRows.map((row) => {
                    const busyRow =
                      schoolActionId != null && String(schoolActionId) === String(row.orgId);
                    return (
                      <tr
                        key={row.key}
                        className={row.mapped ? 'pickup-hubs-table__row--mapped' : ''}
                      >
                        <td>
                          <div className="pickup-hubs-school__name">{row.name}</div>
                        </td>
                        <td>{row.addressText}</td>
                        <td>{formatDistanceKm(row.distanceKm) || '—'}</td>
                        <td>
                          {row.mapped ? (
                            row.isPrimary ? (
                              <span className="pickup-hubs-badge">Primary</span>
                            ) : (
                              <span className="pickup-hubs-muted">Secondary</span>
                            )
                          ) : (
                            <span className="pickup-hubs-muted">—</span>
                          )}
                        </td>
                        <td className="pickup-hubs-table__icon-col">
                          <MapsLink
                            lat={row.lat}
                            lon={row.lon}
                            addressText={row.addressText}
                            label={row.name}
                            onOpen={setMapPreview}
                          />
                        </td>
                        <td>
                          {row.mapped ? (
                            <div className="pickup-hubs-school__actions">
                              {!row.isPrimary ? (
                                <button
                                  type="button"
                                  className="eats-admin-btn eats-admin-btn--secondary"
                                  onClick={() => handleSetPrimary(row.orgId)}
                                  disabled={busy || busyRow}
                                >
                                  {busyRow ? 'Saving…' : 'Make primary'}
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="eats-admin-btn eats-admin-btn--secondary"
                                onClick={() => handleUnmapSchool(row.orgId)}
                                disabled={busy || busyRow}
                              >
                                {busyRow ? 'Removing…' : 'Remove'}
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="eats-admin-btn eats-admin-btn--primary"
                              onClick={() => handleMapSchool(row.orgId)}
                              disabled={busy || busyRow}
                            >
                              {busyRow ? 'Adding…' : 'Add'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {activeTab === 'hub2hub' && selectedHub ? (
        <>
          <section className="pickup-hubs-section">
            <div className="pickup-hubs-section-heading">
              <h3>Capabilities</h3>
              <InfoHint label="Hub2Hub info">
                Configure hub capabilities and outbound transfer partners. Route B only uses
                allowlisted partners.
              </InfoHint>
            </div>
            <div className="pickup-hubs-grid">
              <label className="pickup-hubs-field">
                Service radius (km)
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={form.service_radius_km}
                  onChange={(e) => setForm((f) => ({ ...f, service_radius_km: e.target.value }))}
                />
              </label>
              <label className="pickup-hubs-field">
                Daily capacity
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.daily_capacity}
                  onChange={(e) => setForm((f) => ({ ...f, daily_capacity: e.target.value }))}
                />
              </label>
            </div>
            <div className="pickup-hubs-checks">
              <label>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
                Active
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.accepts_home_pickup}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, accepts_home_pickup: e.target.checked }))
                  }
                />
                Accepts home pickup
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.accepts_transfer_in}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, accepts_transfer_in: e.target.checked }))
                  }
                />
                Accepts transfer in
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.allows_transfer_out || partnerIds.length > 0}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, allows_transfer_out: e.target.checked }))
                  }
                />
                Allows transfer out
              </label>
            </div>
          </section>

          <section className="pickup-hubs-section">
            <div className="pickup-hubs-section-heading">
              <h3>Outbound transfer partners</h3>
              <InfoHint label="Transfer partners info">
                Boxes picked at {hubNameOf(selectedHub)} may transfer only to checked hubs
                (typically the school&apos;s primary hub).
              </InfoHint>
            </div>
            {!otherHubs.length ? (
              <div className="pickup-hubs-note">No other hubs available.</div>
            ) : (
              <div className="pickup-hubs-partners">
                {otherHubs.map((hub) => {
                  const id = Number(hubIdOf(hub));
                  return (
                    <label key={id} className="pickup-hubs-partner">
                      <input
                        type="checkbox"
                        checked={partnerIds.includes(id)}
                        onChange={() => togglePartner(id)}
                      />
                      <span>
                        {hubNameOf(hub)} <small>#{id}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </section>

          <div className="pickup-hubs-actions">
            <button
              type="button"
              className="eats-admin-btn eats-admin-btn--primary"
              onClick={handleSave}
              disabled={saving || adding || bootstrapping || mappingSchool}
            >
              {saving ? 'Saving…' : 'Save hub settings'}
            </button>
          </div>
        </>
      ) : null}

      {activeTab === 'pricing' ? (
        <div className="pickup-hubs-pricing-embed">
          <PickupPricingPage />
        </div>
      ) : null}

      <MapPreviewModal
        open={Boolean(mapPreview)}
        title={mapPreview?.title}
        embedUrl={mapPreview?.embedUrl}
        externalUrl={mapPreview?.externalUrl}
        onClose={closeMapPreview}
      />
    </div>
  );
};

export default PickupHubsPage;
